'use client';

import { useEffect, useState } from 'react';
import { LockClosedIcon, ShieldCheckIcon } from '@heroicons/react/24/solid';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';

interface SSLSecurityIndicatorProps {
  currentLanguage: string;
}

const SSLSecurityIndicator = ({ currentLanguage }: SSLSecurityIndicatorProps) => {
  const [isSecure, setIsSecure] = useState(false);
  const [protocol, setProtocol] = useState('');

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const protocol = window.location.protocol;
      setProtocol(protocol);
      setIsSecure(protocol === 'https:');
    }
  }, []);

  const content = {
    en: {
      secure: 'Secure SSL Connection',
      secureDesc: 'Your connection is encrypted with industry-standard SSL/TLS protocols',
      insecure: 'Unsecured Connection',
      insecureDesc: 'Warning: Your connection is not encrypted. Please use HTTPS.',
      protocol: 'Protocol',
      encryption: 'AES-256 Encryption Active',
    },
    hi: {
      secure: 'सुरक्षित SSL कनेक्शन',
      secureDesc: 'आपका कनेक्शन उद्योग-मानक SSL/TLS प्रोटोकॉल से एन्क्रिप्टेड है',
      insecure: 'असुरक्षित कनेक्शन',
      insecureDesc: 'चेतावनी: आपका कनेक्शन एन्क्रिप्टेड नहीं है। कृपया HTTPS का उपयोग करें।',
      protocol: 'प्रोटोकॉल',
      encryption: 'AES-256 एन्क्रिप्शन सक्रिय',
    },
    te: {
      secure: 'సురక్షిత SSL కనెక్షన్',
      secureDesc: 'మీ కనెక్షన్ పరిశ్రమ-ప్రమాణ SSL/TLS ప్రోటోకాల్స్‌తో ఎన్క్రిప్ట్ చేయబడింది',
      insecure: 'అసురక్షిత కనెక్షన్',
      insecureDesc: 'హెచ్చరిక: మీ కనెక్షన్ ఎన్క్రిప్ట్ చేయబడలేదు. దయచేసి HTTPSని ఉపయోగించండి.',
      protocol: 'ప్రోటోకాల్',
      encryption: 'AES-256 ఎన్క్రిప్షన్ చురుకుగా ఉంది',
    },
  };

  const lang = content[currentLanguage as keyof typeof content] || content.en;

  return (
    <div className={`fixed top-24 left-4 z-40 ${isSecure ? 'bg-green-100 border-green-500' : 'bg-red-100 border-red-500'} border-2 rounded-lg shadow-lg p-3 max-w-xs`}>
      <div className="flex items-start gap-3">
        {isSecure ? (
          <ShieldCheckIcon className="h-6 w-6 text-green-600 flex-shrink-0" />
        ) : (
          <ExclamationTriangleIcon className="h-6 w-6 text-red-600 flex-shrink-0" />
        )}
        <div>
          <div className="flex items-center gap-2 mb-1">
            <LockClosedIcon className={`h-4 w-4 ${isSecure ? 'text-green-600' : 'text-red-600'}`} />
            <h3 className={`font-bold text-sm ${isSecure ? 'text-green-900' : 'text-red-900'}`}>
              {isSecure ? lang.secure : lang.insecure}
            </h3>
          </div>
          <p className={`text-xs ${isSecure ? 'text-green-700' : 'text-red-700'} mb-2`}>
            {isSecure ? lang.secureDesc : lang.insecureDesc}
          </p>
          <div className="flex flex-col gap-1">
            <span className={`text-xs font-mono ${isSecure ? 'text-green-800' : 'text-red-800'}`}>
              {lang.protocol}: {protocol.toUpperCase()}
            </span>
            {isSecure && (
              <span className="text-xs text-green-700 font-semibold">
                ✓ {lang.encryption}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SSLSecurityIndicator;